import React from 'react';
import PropTypes from 'prop-types';

import {Avatar, Box, Checkbox, Chip, Link, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import CaregiverProfileDialog from '../caregiver_profile/CaregiverProfileDialog';
import RequirementStatus from './components/RequirementStatus';

import moment from 'moment';
import contrast from 'css/Contrast';
import {convertToE164} from 'utils/miscellaneous/e164';

const NearbyCaregiverCard = (props) => {
  const {
    applicationState,
    bio,
    canTravel,
    caregiver,
    certificationStatus,
    considerationStatus,
    distance,
    initialOutreach,
    languageStatus,
  } = props.item;
  const {isDisabled, isSelected, onSelectionChange} = props;
  const nameFromBio = [bio.firstName, bio.lastName].filter(Boolean).join(' ');
  const caregiverName = caregiver.caregiverName || nameFromBio || translate('caregiver.CaregiverList.DefaultName');
  const profilePhoto = bio.profilePhoto?.[0]?.src;
  const city = bio.city;
  const phoneNumber = bio.phoneNumber;
  const hasValidPhoneNumber = Boolean(convertToE164(phoneNumber).e164Format);
  const statusColor = translate(`caregiver.Status.${caregiver.status}.Color`);
  const statusTextColor = contrast(statusColor);
  const emailSentAt = initialOutreach?.email?.sentAt;
  const smsSentAt = initialOutreach?.sms?.sentAt;
  const [isProfileOpen, setIsProfileOpen] = React.useState(false);

  const openProfile = () => {
    setIsProfileOpen(true);
  };

  const closeProfile = () => {
    setIsProfileOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '48px 56px minmax(150px, 1.3fr) minmax(160px, 1.2fr) 145px 130px 120px 130px 130px 100px 120px 180px',
          gap: 1.5,
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: isSelected ? '#FFF7F4' : 'transparent',
          px: 2,
          py: 1.5,
        }}
      >
        {
          hasValidPhoneNumber ?
            <Checkbox
              label=""
              ariaLabel={`Select ${caregiverName}`}
              checked={isSelected}
              disabled={isDisabled}
              onChange={onSelectionChange}
              formControlSx={{m: 0}}
              checkBoxSx={{p: 0.5}}
            /> :
            <Box />
        }
        <Avatar alt={caregiverName} src={profilePhoto} sx={{width: 44, height: 44}} />
        <Link onClick={openProfile} sx={{textAlign: 'left'}}>
          <Typography fontWeight={600}>{caregiverName}</Typography>
        </Link>
        <Typography variant="body2">
          {
          city ?
          <Translate
            expression="career.jobDetail.NearbyCaregivers.CityDistance"
            substitute={{city, distance: distance.toFixed(1)}}
          /> :
          <Translate
            expression="career.jobDetail.NearbyCaregivers.Distance"
            substitute={{distance: distance.toFixed(1)}}
          />
          }
        </Typography>
        <Typography variant="body2">
          {
            phoneNumber ||
          <Translate expression="career.jobDetail.NearbyCaregivers.Status.NotAvailable" />
          }
        </Typography>
        <Chip
          label={<Translate expression={`caregiver.Status.${caregiver.status}.Title`} />}
          sx={{
            backgroundColor: statusColor,
            color: statusTextColor,
          }}
          size="small"
        />
        <RequirementStatus status={languageStatus} />
        <RequirementStatus status={certificationStatus} />
        <RequirementStatus status={considerationStatus} />
        <Chip
          label={<Translate expression={`career.jobDetail.NearbyCaregivers.CanTravel.${String(canTravel)}`} />}
          color={canTravel === true ? 'success' : canTravel === false ? 'error' : 'default'}
          size="small"
        />
        <Chip
          label={<Translate expression={`career.jobDetail.NearbyCaregivers.Application.${applicationState}`} />}
          color={applicationState === 'ACCEPTED' ? 'success' : applicationState === 'REJECTED' ? 'error' : 'default'}
          size="small"
        />
        <Typography variant="body2">
          {
            emailSentAt || smsSentAt ?
              <Stack spacing={0.25}>
                {
                  emailSentAt &&
                  <Translate
                    expression="career.jobDetail.NearbyCaregivers.InitialOutreach.EmailSentAt"
                    substitute={{date: moment(emailSentAt).format('MMM D, YYYY h:mm A')}}
                  />
                }
                {
                  smsSentAt &&
                  <Translate
                    expression="career.jobDetail.NearbyCaregivers.InitialOutreach.SmsSentAt"
                    substitute={{date: moment(smsSentAt).format('MMM D, YYYY h:mm A')}}
                  />
                }
              </Stack> :
              <Translate expression="career.jobDetail.NearbyCaregivers.InitialOutreach.NotSent" />
          }
        </Typography>
      </Box>
      {
        isProfileOpen &&
        <CaregiverProfileDialog
          caregiverName={caregiverName}
          caregiverRn={caregiver.caregiverRn}
          initialBio={bio}
          onClose={closeProfile}
          open={isProfileOpen}
        />
      }
    </>
  );
};

NearbyCaregiverCard.propTypes = {
  isDisabled: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  item: PropTypes.shape({
    applicationState: PropTypes.string.isRequired,
    bio: PropTypes.object.isRequired,
    canTravel: PropTypes.bool,
    caregiver: PropTypes.object.isRequired,
    certificationStatus: PropTypes.bool,
    considerationStatus: PropTypes.bool,
    distance: PropTypes.number.isRequired,
    initialOutreach: PropTypes.object,
    languageStatus: PropTypes.bool,
  }).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
};

export default React.memo(NearbyCaregiverCard);
